import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Iconoir icons - https://iconoir.com/
import {
  Home,
  Folder,
  Page,
  EmptyPage,
  Settings,
  Trash,
  Plus,
  Xmark,
  WarningTriangle,
  Check,
  InfoCircle,
  Upload,
  Flash,
  Package,
  NavArrowLeft,
  Server,
  Terminal as TerminalIcon,
  Code,
  Box3dCenter
} from 'iconoir-react';

// Import design system components
import {
  MinimalBackgroundSystem,
  GlassContainer,
  GlassCard,
  MetricCard,
  Button,
  StatusIndicator,
  Toggle,
  Input,
  Badge,
  ProgressBar,
  DotMatrix,
  Terminal,
  ShellName,
  UptimeCounter,
  Cursor,
  Spinner,
  Skeleton,
  PageLayout,
  ThemeUtils,
  SHELL_DESIGN_SYSTEM_VERSION
} from '../shell-design-system';

// Available themes
const THEMES = [
  { id: 'dark', name: 'Default Dark' },
  { id: 'light', name: 'Light' },
  { id: 'dracula', name: 'Dracula' },
  { id: 'tokyo-night', name: 'Tokyo Night' },
  { id: 'github-dark', name: 'GitHub Dark' },
  { id: 'monokai', name: 'Monokai' },
  { id: 'nord', name: 'Nord' },
  { id: 'warp', name: 'Warp' }
];

// Section component - uses tokenized padding (minimum 12px / var(--space-3))
const Section = ({ title, description, children }) => (
  <section style={{ marginBottom: 'var(--space-8)' }}>
    <div className="command-block" style={{
      marginBottom: 'var(--space-6)',
      padding: 'var(--space-3)',
      backgroundColor: 'var(--surface-secondary)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--surface-border)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
        <span style={{ color: 'var(--prompt-color)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xl)', fontWeight: '700' }}>$</span>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: '700', fontFamily: 'var(--font-mono)', margin: 0, color: 'var(--text-primary)' }}>{title}</h2>
      </div>
      {description && (
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', margin: 0, paddingLeft: 'var(--space-7)', fontFamily: 'var(--font-mono)' }}>
          # {description}
        </p>
      )}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
      {children}
    </div>
  </section>
);

// Component Card - uses tokenized padding (minimum 12px / var(--space-3))
const ComponentCard = ({ title, children, code, wide }) => (
  <div style={{
    backgroundColor: 'var(--surface-primary)',
    border: '1px solid var(--surface-border)',
    borderRadius: 'var(--radius-lg)',
    minWidth: wide ? '400px' : '280px',
    maxWidth: wide ? '600px' : '400px',
    flex: wide ? '1 1 500px' : '1 1 300px',
    overflow: 'hidden'
  }}>
    <div style={{
      padding: 'var(--space-4) var(--space-5)',
      backgroundColor: 'var(--surface-secondary)',
      borderBottom: '1px solid var(--surface-border)',
      fontFamily: 'var(--font-mono)',
      fontSize: 'var(--text-sm)',
      color: 'var(--text-primary)',
      fontWeight: '500',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center'
    }}>
      {title}
    </div>
    <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', alignItems: 'center' }}>
      {children}
    </div>
    {code && (
      <div style={{
        backgroundColor: 'var(--command-bg)',
        color: 'var(--ansi-cyan)',
        padding: 'var(--space-3) var(--space-4)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-xs)',
        borderTop: '1px solid var(--surface-border)'
      }}>
        <code>{code}</code>
      </div>
    )}
  </div>
);

// Theme Selector - uses tokenized padding
const ThemeSelector = ({ currentTheme, onThemeChange }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', justifyContent: 'center', padding: 'var(--space-2)' }}>
    {THEMES.map(theme => (
      <button
        key={theme.id}
        onClick={() => onThemeChange(theme.id)}
        className={`btn ${currentTheme === theme.id ? 'btn--primary' : 'btn--secondary'}`}
        style={{ minWidth: '100px' }}
      >
        {theme.name}
      </button>
    ))}
  </div>
);

// Main Component
const DesignSystemShowcase = () => {
  const [currentTheme, setCurrentTheme] = useState('dark');
  const [toggleState, setToggleState] = useState(false);
  const [checkboxState, setCheckboxState] = useState(false);
  const [radioValue, setRadioValue] = useState('option1');
  const [sliderValue, setSliderValue] = useState(50);
  const [inputValue, setInputValue] = useState('');
  const [selectOpen, setSelectOpen] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  return (
    <MinimalBackgroundSystem showGrid={true}>
      <div style={{ minHeight: '100vh', padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>

        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: 'var(--space-8)', padding: 'var(--space-7)' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
            <NavArrowLeft width={16} height={16} /> Back to Shell
          </Link>
          <h1 style={{ fontSize: 'var(--text-4xl)', fontWeight: '700', fontFamily: 'var(--font-mono)', color: 'var(--accent-primary)', marginBottom: '8px' }}>
            Shell Design System
          </h1>
          <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: '24px' }}>
            v5.0 • Terminal-first • Mobile-optimized • Flat Design
          </p>
          <ThemeSelector currentTheme={currentTheme} onThemeChange={setCurrentTheme} />
        </header>

        {/* BUTTONS */}
        <Section title="Buttons" description="Interactive buttons with variants, sizes, and states">
          <ComponentCard title="Button Variants" code="className='btn btn--{variant}'">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
              <button className="btn btn--primary">Primary</button>
              <button className="btn btn--secondary">Secondary</button>
              <button className="btn btn--ghost">Ghost</button>
              <button className="btn btn--outline">Outline</button>
            </div>
          </ComponentCard>

          <ComponentCard title="Semantic Buttons" code="btn--success | btn--danger | btn--warning">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
              <button className="btn btn--success">Success</button>
              <button className="btn btn--danger">Danger</button>
              <button className="btn btn--warning">Warning</button>
            </div>
          </ComponentCard>

          <ComponentCard title="Button Sizes" code="btn--sm | btn--lg | btn--xl">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
              <button className="btn btn--primary btn--sm">Small</button>
              <button className="btn btn--primary">Default</button>
              <button className="btn btn--primary btn--lg">Large</button>
            </div>
          </ComponentCard>

          <ComponentCard title="Button States" code="disabled">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
              <button className="btn btn--primary">Normal</button>
              <button className="btn btn--primary" disabled>Disabled</button>
            </div>
          </ComponentCard>

          <ComponentCard title="Icon Button" code="btn--icon">
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center' }}>
              <button className="btn btn--secondary btn--icon"><Xmark width={18} height={18} /></button>
              <button className="btn btn--secondary btn--icon"><Plus width={18} height={18} /></button>
              <button className="btn btn--secondary btn--icon"><Settings width={18} height={18} /></button>
            </div>
          </ComponentCard>

          <ComponentCard title="Button Group" code="btn-group">
            <div className="btn-group">
              <button className="btn btn--secondary">Left</button>
              <button className="btn btn--secondary">Center</button>
              <button className="btn btn--secondary">Right</button>
            </div>
          </ComponentCard>
        </Section>

        {/* TOGGLE / SWITCH */}
        <Section title="Toggle / Switch" description="Binary on/off controls">
          <ComponentCard title="Toggle States" code="className='toggle toggle--checked'">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <label className="toggle">
                <div className="toggle-track">
                  <div className="toggle-thumb"></div>
                </div>
                <span className="toggle-label">Off State</span>
              </label>
              <label className="toggle toggle--checked">
                <div className="toggle-track">
                  <div className="toggle-thumb"></div>
                </div>
                <span className="toggle-label">On State</span>
              </label>
              <label className="toggle toggle--disabled">
                <div className="toggle-track">
                  <div className="toggle-thumb"></div>
                </div>
                <span className="toggle-label">Disabled</span>
              </label>
            </div>
          </ComponentCard>

          <ComponentCard title="Interactive Toggle" code="onClick handler">
            <label className={`toggle ${toggleState ? 'toggle--checked' : ''}`} onClick={() => setToggleState(!toggleState)}>
              <div className="toggle-track">
                <div className="toggle-thumb"></div>
              </div>
              <span className="toggle-label">{toggleState ? 'Enabled' : 'Disabled'}</span>
            </label>
          </ComponentCard>

          <ComponentCard title="Toggle Button Group" code="toggle-group">
            <div className="toggle-group">
              <button className="toggle-group-item toggle-group-item--active">Day</button>
              <button className="toggle-group-item">Week</button>
              <button className="toggle-group-item">Month</button>
            </div>
          </ComponentCard>
        </Section>

        {/* CHECKBOX */}
        <Section title="Checkbox" description="Multi-select options">
          <ComponentCard title="Checkbox States" code="className='checkbox checkbox--checked'">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <label className="checkbox">
                <div className="checkbox-box">
                  <span className="checkbox-check">✓</span>
                </div>
                <span className="checkbox-label">Unchecked</span>
              </label>
              <label className="checkbox checkbox--checked">
                <div className="checkbox-box">
                  <span className="checkbox-check">✓</span>
                </div>
                <span className="checkbox-label">Checked</span>
              </label>
              <label className="checkbox checkbox--disabled">
                <div className="checkbox-box">
                  <span className="checkbox-check">✓</span>
                </div>
                <span className="checkbox-label">Disabled</span>
              </label>
            </div>
          </ComponentCard>

          <ComponentCard title="Interactive Checkbox" code="onClick handler">
            <label className={`checkbox ${checkboxState ? 'checkbox--checked' : ''}`} onClick={() => setCheckboxState(!checkboxState)}>
              <div className="checkbox-box">
                <span className="checkbox-check">✓</span>
              </div>
              <span className="checkbox-label">Accept terms and conditions</span>
            </label>
          </ComponentCard>
        </Section>

        {/* RADIO */}
        <Section title="Radio Button" description="Single-select options">
          <ComponentCard title="Radio Group" code="className='radio radio--checked'">
            <div className="radio-group" style={{ width: '100%' }}>
              {['option1', 'option2', 'option3'].map((opt, i) => (
                <label
                  key={opt}
                  className={`radio ${radioValue === opt ? 'radio--checked' : ''}`}
                  onClick={() => setRadioValue(opt)}
                >
                  <div className="radio-circle">
                    <div className="radio-dot"></div>
                  </div>
                  <span className="radio-label">Option {i + 1}</span>
                </label>
              ))}
            </div>
          </ComponentCard>

          <ComponentCard title="Horizontal Radio" code="radio-group--horizontal">
            <div className="radio-group radio-group--horizontal">
              <label className="radio radio--checked">
                <div className="radio-circle"><div className="radio-dot"></div></div>
                <span className="radio-label">S</span>
              </label>
              <label className="radio">
                <div className="radio-circle"><div className="radio-dot"></div></div>
                <span className="radio-label">M</span>
              </label>
              <label className="radio">
                <div className="radio-circle"><div className="radio-dot"></div></div>
                <span className="radio-label">L</span>
              </label>
            </div>
          </ComponentCard>
        </Section>

        {/* TEXT INPUT */}
        <Section title="Text Input" description="Text entry fields with states">
          <ComponentCard title="Input States" code="className='input'">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <input className="input" placeholder="Default input" />
              <input className="input" value="With value" readOnly />
              <input className="input input--error" placeholder="Error state" />
              <input className="input" disabled placeholder="Disabled" />
            </div>
          </ComponentCard>

          <ComponentCard title="Input with Label" code="input-group">
            <div className="input-group" style={{ width: '100%' }}>
              <label className="input-label">Email Address</label>
              <input className="input" type="email" placeholder="you@example.com" />
              <span className="input-hint">We'll never share your email.</span>
            </div>
          </ComponentCard>

          <ComponentCard title="Search Input" code="input--search">
            <input className="input input--search" placeholder="Search..." style={{ width: '100%' }} />
          </ComponentCard>
        </Section>

        {/* TEXTAREA */}
        <Section title="Textarea" description="Multi-line text entry">
          <ComponentCard title="Textarea" code="className='textarea'" wide>
            <textarea className="textarea" placeholder="Enter your message..." style={{ width: '100%' }}></textarea>
          </ComponentCard>
        </Section>

        {/* NUMBER INPUT */}
        <Section title="Number Input" description="Numeric input with increment/decrement">
          <ComponentCard title="Number Input" code="className='number-input'">
            <div className="number-input" style={{ width: '160px' }}>
              <button className="number-input-btn">−</button>
              <input type="number" className="number-input-field" value="5" readOnly />
              <button className="number-input-btn">+</button>
            </div>
          </ComponentCard>
        </Section>

        {/* SELECT / DROPDOWN */}
        <Section title="Select / Dropdown" description="Selection from options">
          <ComponentCard title="Select" code="className='select'">
            <div className={`select ${selectOpen ? 'select--open' : ''}`} style={{ width: '100%' }}>
              <div className="select-trigger" onClick={() => setSelectOpen(!selectOpen)}>
                <span>Select an option</span>
                <span className="select-arrow">▼</span>
              </div>
              <div className="select-menu">
                <div className="select-option">Option 1</div>
                <div className="select-option select-option--selected">Option 2</div>
                <div className="select-option">Option 3</div>
              </div>
            </div>
          </ComponentCard>

          <ComponentCard title="Native Select" code="select.input">
            <select className="input" style={{ width: '100%' }}>
              <option>Option 1</option>
              <option>Option 2</option>
              <option>Option 3</option>
            </select>
          </ComponentCard>
        </Section>

        {/* SLIDER / RANGE */}
        <Section title="Slider / Range" description="Value selection within range">
          <ComponentCard title="Range Slider" code="input[type='range']">
            <div style={{ width: '100%' }}>
              <div className="slider-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span className="slider-label">Volume</span>
                <span className="slider-value">{sliderValue}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={sliderValue}
                onChange={(e) => setSliderValue(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </ComponentCard>
        </Section>

        {/* BADGES / TAGS */}
        <Section title="Badges & Tags" description="Labels and status indicators">
          <ComponentCard title="Badge Variants" code="className='badge badge--{variant}'">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              <span className="badge badge--primary">Primary</span>
              <span className="badge badge--secondary">Secondary</span>
              <span className="badge badge--success">Success</span>
              <span className="badge badge--warning">Warning</span>
              <span className="badge badge--error">Error</span>
              <span className="badge badge--accent">Accent</span>
            </div>
          </ComponentCard>

          <ComponentCard title="Tags (Removable)" code="className='tag'">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              <span className="tag">React <span className="tag-remove">×</span></span>
              <span className="tag">TypeScript <span className="tag-remove">×</span></span>
              <span className="tag">Vite <span className="tag-remove">×</span></span>
            </div>
          </ComponentCard>

          <ComponentCard title="Chips" code="className='chip'">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              <span className="chip">
                <span className="chip-avatar"></span>
                John Doe
                <span className="chip-remove">×</span>
              </span>
              <span className="chip">
                <span className="chip-avatar"></span>
                Jane Smith
              </span>
            </div>
          </ComponentCard>
        </Section>

        {/* STATUS INDICATOR */}
        <Section title="Status Indicator" description="System and process states">
          <ComponentCard title="Status States" code="status-indicator--{state}">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="status-indicator status-indicator--online">
                <span className="status-dot"></span>
                <span>Online</span>
              </div>
              <div className="status-indicator status-indicator--offline">
                <span className="status-dot"></span>
                <span>Offline</span>
              </div>
              <div className="status-indicator status-indicator--loading">
                <span className="status-dot"></span>
                <span>Loading</span>
              </div>
              <div className="status-indicator status-indicator--error">
                <span className="status-dot"></span>
                <span>Error</span>
              </div>
              <div className="status-indicator status-indicator--idle">
                <span className="status-dot"></span>
                <span>Idle</span>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* ALERTS */}
        <Section title="Alerts" description="Contextual feedback messages">
          <ComponentCard title="Alert Variants" code="className='alert alert--{type}'" wide>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <div className="alert alert--info">
                <span className="alert-icon">ℹ</span>
                <div className="alert-content">
                  <div className="alert-title">Information</div>
                  <div className="alert-message">This is an informational message.</div>
                </div>
                <span className="alert-close">×</span>
              </div>
              <div className="alert alert--success">
                <span className="alert-icon">✓</span>
                <div className="alert-content">
                  <div className="alert-title">Success</div>
                  <div className="alert-message">Operation completed successfully.</div>
                </div>
              </div>
              <div className="alert alert--warning">
                <span className="alert-icon">⚠</span>
                <div className="alert-content">
                  <div className="alert-title">Warning</div>
                  <div className="alert-message">Please review before proceeding.</div>
                </div>
              </div>
              <div className="alert alert--error">
                <span className="alert-icon">✕</span>
                <div className="alert-content">
                  <div className="alert-title">Error</div>
                  <div className="alert-message">Something went wrong.</div>
                </div>
              </div>
            </div>
          </ComponentCard>

          <ComponentCard title="Inline Alerts" code="className='inline-alert'">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              <div className="inline-alert inline-alert--info">ℹ Info message</div>
              <div className="inline-alert inline-alert--success">✓ Success message</div>
              <div className="inline-alert inline-alert--warning">⚠ Warning message</div>
              <div className="inline-alert inline-alert--error">✕ Error message</div>
            </div>
          </ComponentCard>
        </Section>

        {/* TOAST / NOTIFICATION */}
        <Section title="Toast / Notification" description="Temporary feedback messages">
          <ComponentCard title="Toast Variants" code="className='toast toast--{type}'" wide>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <div className="toast toast--success" style={{ position: 'relative', animation: 'none' }}>
                <span className="toast-icon">✓</span>
                <div className="toast-content">
                  <div className="toast-title">Success</div>
                  <div className="toast-message">Your changes have been saved.</div>
                </div>
                <span className="toast-close">×</span>
              </div>
              <div className="toast toast--error" style={{ position: 'relative', animation: 'none' }}>
                <span className="toast-icon">✕</span>
                <div className="toast-content">
                  <div className="toast-title">Error</div>
                  <div className="toast-message">Failed to save changes.</div>
                </div>
                <span className="toast-close">×</span>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* PROGRESS BAR */}
        <Section title="Progress Bar" description="Visual progress indication">
          <ComponentCard title="Progress Variants" code="className='progress'">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>Default</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>60%</span>
                </div>
                <div className="progress">
                  <div className="progress-bar" style={{ width: '60%' }}></div>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>Success</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>100%</span>
                </div>
                <div className="progress progress--success">
                  <div className="progress-bar" style={{ width: '100%' }}></div>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>Warning</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>45%</span>
                </div>
                <div className="progress progress--warning">
                  <div className="progress-bar" style={{ width: '45%' }}></div>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>Indeterminate</span>
                </div>
                <div className="progress progress--indeterminate">
                  <div className="progress-bar"></div>
                </div>
              </div>
            </div>
          </ComponentCard>

          <ComponentCard title="Progress Sizes" code="progress--sm | progress--lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <div className="progress progress--sm">
                <div className="progress-bar" style={{ width: '70%' }}></div>
              </div>
              <div className="progress">
                <div className="progress-bar" style={{ width: '70%' }}></div>
              </div>
              <div className="progress progress--lg">
                <div className="progress-bar" style={{ width: '70%' }}></div>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* SPINNER / LOADERS */}
        <Section title="Spinner / Loaders" description="Loading state indicators">
          <ComponentCard title="Spinner Sizes" code="spinner--xs | sm | md | lg | xl">
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', justifyContent: 'center' }}>
              <div className="spinner spinner--xs"></div>
              <div className="spinner spinner--sm"></div>
              <div className="spinner spinner--md"></div>
              <div className="spinner spinner--lg"></div>
              <div className="spinner spinner--xl"></div>
            </div>
          </ComponentCard>

          <ComponentCard title="Dots Loader" code="className='loader-dots'">
            <div className="loader-dots">
              <div className="loader-dot"></div>
              <div className="loader-dot"></div>
              <div className="loader-dot"></div>
            </div>
          </ComponentCard>

          <ComponentCard title="Bar Loader" code="className='loader-bar'">
            <div className="loader-bar" style={{ width: '200px' }}>
              <div className="loader-bar-fill"></div>
            </div>
          </ComponentCard>
        </Section>

        {/* SKELETON */}
        <Section title="Skeleton" description="Content placeholder during loading">
          <ComponentCard title="Skeleton Types" code="skeleton--text | title | avatar | button | card">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div className="skeleton skeleton--avatar"></div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div className="skeleton skeleton--title"></div>
                  <div className="skeleton skeleton--text"></div>
                </div>
              </div>
              <div className="skeleton skeleton--button"></div>
              <div className="skeleton skeleton--card"></div>
            </div>
          </ComponentCard>
        </Section>

        {/* DIALOG / MODAL */}
        <Section title="Dialog / Modal" description="Overlay windows for focused content">
          <ComponentCard title="Dialog Example" code="className='dialog-overlay'">
            <button className="btn btn--primary" onClick={() => setDialogOpen(true)}>Open Dialog</button>
            {dialogOpen && (
              <div className="dialog-overlay dialog-overlay--open" onClick={() => setDialogOpen(false)} style={{ position: 'fixed' }}>
                <div className="dialog" onClick={e => e.stopPropagation()}>
                  <div className="dialog-header">
                    <span className="dialog-title">Confirm Action</span>
                    <button className="dialog-close" onClick={() => setDialogOpen(false)}>×</button>
                  </div>
                  <div className="dialog-content">
                    <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                      Are you sure you want to proceed with this action? This cannot be undone.
                    </p>
                  </div>
                  <div className="dialog-footer">
                    <button className="btn btn--secondary" onClick={() => setDialogOpen(false)}>Cancel</button>
                    <button className="btn btn--primary" onClick={() => setDialogOpen(false)}>Confirm</button>
                  </div>
                </div>
              </div>
            )}
          </ComponentCard>
        </Section>

        {/* TABS */}
        <Section title="Tabs" description="Tabbed content navigation">
          <ComponentCard title="Default Tabs" code="className='tabs'" wide>
            <div className="tabs" style={{ width: '100%' }}>
              <div className="tabs-list">
                <button className="tab tab--active">Overview</button>
                <button className="tab">Settings</button>
                <button className="tab">Logs</button>
                <button className="tab" disabled>Disabled</button>
              </div>
              <div className="tab-panel">
                <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>Tab content goes here.</p>
              </div>
            </div>
          </ComponentCard>

          <ComponentCard title="Pills Tabs" code="tabs--pills" wide>
            <div className="tabs tabs--pills" style={{ width: '100%' }}>
              <div className="tabs-list">
                <button className="tab tab--active">Day</button>
                <button className="tab">Week</button>
                <button className="tab">Month</button>
                <button className="tab">Year</button>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* ACCORDION */}
        <Section title="Accordion / Collapse" description="Expandable content sections">
          <ComponentCard title="Accordion" code="className='accordion'" wide>
            <div className="accordion" style={{ width: '100%' }}>
              <div className={`accordion-item ${accordionOpen ? 'accordion-item--open' : ''}`}>
                <button className="accordion-trigger" onClick={() => setAccordionOpen(!accordionOpen)}>
                  <span>What is Shell Design System?</span>
                  <span className="accordion-icon">▼</span>
                </button>
                <div className="accordion-content">
                  <div className="accordion-body">
                    <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: 0 }}>
                      Shell Design System is a terminal-first, mobile-optimized component library with flat design principles.
                    </p>
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <button className="accordion-trigger">
                  <span>How do I customize themes?</span>
                  <span className="accordion-icon">▼</span>
                </button>
                <div className="accordion-content">
                  <div className="accordion-body">
                    <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: 0 }}>
                      Use CSS custom properties to customize colors, spacing, and other tokens.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* BREADCRUMB */}
        <Section title="Breadcrumb" description="Navigation path indicator">
          <ComponentCard title="Breadcrumb" code="className='breadcrumb'" wide>
            <nav className="breadcrumb">
              <a className="breadcrumb-item" href="#">Home</a>
              <span className="breadcrumb-separator">/</span>
              <a className="breadcrumb-item" href="#">Shells</a>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-item breadcrumb-item--current">host</span>
            </nav>
          </ComponentCard>
        </Section>

        {/* PAGINATION */}
        <Section title="Pagination" description="Page navigation">
          <ComponentCard title="Pagination" code="className='pagination'">
            <nav className="pagination">
              <button className="pagination-item">←</button>
              <button className="pagination-item">1</button>
              <button className="pagination-item pagination-item--active">2</button>
              <button className="pagination-item">3</button>
              <span className="pagination-ellipsis">...</span>
              <button className="pagination-item">10</button>
              <button className="pagination-item">→</button>
            </nav>
          </ComponentCard>
        </Section>

        {/* STEPPER */}
        <Section title="Stepper" description="Multi-step process indicator">
          <ComponentCard title="Horizontal Stepper" code="className='stepper'" wide>
            <div className="stepper" style={{ width: '100%' }}>
              <div className="stepper-item stepper-item--completed">
                <div className="stepper-indicator">✓</div>
                <div className="stepper-content">
                  <div className="stepper-title">Setup</div>
                </div>
              </div>
              <div className="stepper-connector"></div>
              <div className="stepper-item stepper-item--active">
                <div className="stepper-indicator">2</div>
                <div className="stepper-content">
                  <div className="stepper-title">Configure</div>
                </div>
              </div>
              <div className="stepper-connector"></div>
              <div className="stepper-item">
                <div className="stepper-indicator">3</div>
                <div className="stepper-content">
                  <div className="stepper-title">Deploy</div>
                </div>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* AVATAR */}
        <Section title="Avatar" description="User profile images">
          <ComponentCard title="Avatar Sizes" code="avatar--xs | sm | md | lg | xl">
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'center' }}>
              <div className="avatar avatar--xs">XS</div>
              <div className="avatar avatar--sm">SM</div>
              <div className="avatar avatar--md">MD</div>
              <div className="avatar avatar--lg">LG</div>
              <div className="avatar avatar--xl">XL</div>
            </div>
          </ComponentCard>

          <ComponentCard title="Avatar Group" code="className='avatar-group'">
            <div className="avatar-group">
              <div className="avatar">A</div>
              <div className="avatar">B</div>
              <div className="avatar">C</div>
              <div className="avatar">+3</div>
            </div>
          </ComponentCard>
        </Section>

        {/* TOOLTIP */}
        <Section title="Tooltip" description="Contextual information on hover">
          <ComponentCard title="Tooltip" code="className='tooltip'">
            <div className="tooltip">
              <button className="btn btn--secondary">Hover me</button>
              <div className="tooltip-content">This is a tooltip</div>
            </div>
          </ComponentCard>
        </Section>

        {/* DIVIDER */}
        <Section title="Divider" description="Content separation">
          <ComponentCard title="Dividers" code="className='divider'" wide>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: 0 }}>Content above</p>
              <div className="divider"></div>
              <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: 0 }}>Content below</p>
              <div className="divider divider--with-text">
                <span className="divider-text">or</span>
              </div>
              <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: 0 }}>More content</p>
            </div>
          </ComponentCard>
        </Section>

        {/* MENU */}
        <Section title="Menu" description="Dropdown menu options">
          <ComponentCard title="Menu" code="className='menu'">
            <div className="menu" style={{ width: '200px' }}>
              <div className="menu-label">Actions</div>
              <div className="menu-item menu-item--active">
                <span className="menu-item-icon"><Page width={16} height={16} /></span>
                <span>New File</span>
                <span className="menu-item-shortcut">⌘N</span>
              </div>
              <div className="menu-item">
                <span className="menu-item-icon"><Folder width={16} height={16} /></span>
                <span>Open</span>
                <span className="menu-item-shortcut">⌘O</span>
              </div>
              <div className="menu-separator"></div>
              <div className="menu-item menu-item--danger">
                <span className="menu-item-icon"><Trash width={16} height={16} /></span>
                <span>Delete</span>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* EMPTY STATE */}
        <Section title="Empty State" description="No content placeholder">
          <ComponentCard title="Empty State" code="className='empty-state'" wide>
            <div className="empty-state">
              <div className="empty-state-icon"><Box3dCenter width={48} height={48} strokeWidth={1.5} /></div>
              <div className="empty-state-title">No shells found</div>
              <div className="empty-state-description">
                You haven't created any shells yet. Get started by creating your first shell.
              </div>
              <div className="empty-state-actions">
                <button className="btn btn--primary">Create Shell</button>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* ERROR STATE */}
        <Section title="Error State" description="Error display">
          <ComponentCard title="Error State" code="className='error-state'" wide>
            <div className="error-state">
              <div className="error-state-icon"><WarningTriangle width={48} height={48} strokeWidth={1.5} /></div>
              <div className="error-state-title">Something went wrong</div>
              <div className="error-state-description">
                We encountered an error while loading this page. Please try again.
              </div>
              <div className="empty-state-actions">
                <button className="btn btn--danger">Retry</button>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* SUCCESS STATE */}
        <Section title="Success State" description="Success display">
          <ComponentCard title="Success State" code="className='success-state'" wide>
            <div className="success-state">
              <div className="success-state-icon"><Check width={48} height={48} strokeWidth={1.5} /></div>
              <div className="success-state-title">Shell Created</div>
              <div className="success-state-description">
                Your shell has been created successfully and is now running.
              </div>
              <div className="empty-state-actions">
                <button className="btn btn--success">View Shell</button>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* RATING */}
        <Section title="Rating" description="Star rating component">
          <ComponentCard title="Rating" code="className='rating'">
            <div className="rating">
              <span className="rating-star rating-star--filled">★</span>
              <span className="rating-star rating-star--filled">★</span>
              <span className="rating-star rating-star--filled">★</span>
              <span className="rating-star rating-star--filled">★</span>
              <span className="rating-star">★</span>
            </div>
          </ComponentCard>
        </Section>

        {/* FILE UPLOADER */}
        <Section title="File Uploader" description="Drag & drop file upload">
          <ComponentCard title="File Uploader" code="className='file-uploader'" wide>
            <div className="file-uploader" style={{ width: '100%' }}>
              <div className="file-uploader-icon"><Upload width={40} height={40} strokeWidth={1.5} /></div>
              <div className="file-uploader-text">Drag and drop files here</div>
              <div className="file-uploader-hint">or click to browse</div>
            </div>
          </ComponentCard>
        </Section>

        {/* TABLE */}
        <Section title="Table" description="Data tables">
          <ComponentCard title="Table" code="className='table'" wide>
            <div className="table-container" style={{ width: '100%' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Uptime</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>host</td>
                    <td><span className="badge badge--success">Online</span></td>
                    <td>24h 15m</td>
                  </tr>
                  <tr>
                    <td>test_ccc</td>
                    <td><span className="badge badge--success">Online</span></td>
                    <td>12h 30m</td>
                  </tr>
                  <tr>
                    <td>dev</td>
                    <td><span className="badge badge--warning">Idle</span></td>
                    <td>1h 5m</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ComponentCard>
        </Section>

        {/* TERMINAL */}
        <Section title="Terminal" description="CLI-style output display">
          <ComponentCard title="Terminal Window" code="className='terminal'" wide>
            <div className="terminal" style={{ width: '100%' }}>
              <div className="terminal-header">
                <span className="terminal-title">shell@host:~</span>
              </div>
              <div className="terminal-content">
                <div className="terminal-line terminal-line--command">
                  <span className="terminal-prompt">$</span> ls -la
                </div>
                <div className="terminal-line terminal-line--output">
                  drwxr-xr-x  5 shell shell 4096 Jan 30 12:00 .
                </div>
                <div className="terminal-line terminal-line--output">
                  drwxr-xr-x  3 root  root  4096 Jan 30 11:00 ..
                </div>
                <div className="terminal-line terminal-line--success">
                  Operation completed successfully
                </div>
                <div className="terminal-line terminal-line--error">
                  Error: Permission denied
                </div>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* CARDS */}
        <Section title="Cards" description="Content containers">
          <ComponentCard title="Glass Card" code="className='glass-card'">
            <div className="glass-card" style={{ width: '100%' }}>
              <div className="glass-card-header">Card Title</div>
              <div className="glass-card-content">
                <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: 0 }}>
                  Card content goes here.
                </p>
              </div>
            </div>
          </ComponentCard>

          <ComponentCard title="Card with Footer" code="card-footer">
            <div className="card" style={{ width: '100%' }}>
              <div className="card-header">
                <div className="card-title">Settings</div>
                <div className="card-subtitle">Manage your preferences</div>
              </div>
              <div className="card-content">
                <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: 0 }}>
                  Configure your shell settings.
                </p>
              </div>
              <div className="card-footer">
                <button className="btn btn--secondary">Cancel</button>
                <button className="btn btn--primary">Save</button>
              </div>
            </div>
          </ComponentCard>

          <ComponentCard title="Metric Card" code="className='metric-card'">
            <div className="metric-card" style={{ width: '100%' }}>
              <div className="metric-card-label">Active Shells</div>
              <div className="metric-card-value">42</div>
              <div className="metric-card-trend metric-card-trend--up">↑ 12%</div>
            </div>
          </ComponentCard>
        </Section>

        {/* SHELL COMPONENTS */}
        <Section title="Shell Components" description="Shell-specific displays">
          <ComponentCard title="Shell Name" code="className='shell-name'">
            <div className="shell-name">HOST</div>
          </ComponentCard>

          <ComponentCard title="Cursor" code="className='cursor'">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>Type here</span>
              <span className="cursor"></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>Block</span>
              <span className="cursor cursor--block"></span>
            </div>
          </ComponentCard>

          <ComponentCard title="Dot Matrix" code="className='dot-matrix'">
            <div className="dot-matrix dot-matrix--lg">12:34:56</div>
          </ComponentCard>
        </Section>

        {/* DIRECTORY NAVIGATOR */}
        <Section title="Directory Navigator" description="File system navigation">
          <ComponentCard title="Directory List" code="className='dir-navigator'" wide>
            <div className="dir-navigator" style={{ width: '100%' }}>
              <div className="dir-header">
                <span className="dir-path">/home/shell</span>
              </div>
              <ul className="dir-list">
                <li className="dir-item">
                  <span className="dir-item-icon dir-item-icon--folder"><Folder width={16} height={16} /></span>
                  <span className="dir-item-name">frontend</span>
                  <span className="dir-item-meta">4.2 MB</span>
                </li>
                <li className="dir-item">
                  <span className="dir-item-icon dir-item-icon--folder"><Folder width={16} height={16} /></span>
                  <span className="dir-item-name">backend</span>
                  <span className="dir-item-meta">1.8 MB</span>
                </li>
                <li className="dir-item dir-item--selected">
                  <span className="dir-item-icon dir-item-icon--file"><Page width={16} height={16} /></span>
                  <span className="dir-item-name">README.md</span>
                  <span className="dir-item-meta">2.4 KB</span>
                </li>
                <li className="dir-item">
                  <span className="dir-item-icon dir-item-icon--exec"><Flash width={16} height={16} /></span>
                  <span className="dir-item-name">start.sh</span>
                  <span className="dir-item-meta">512 B</span>
                </li>
              </ul>
            </div>
          </ComponentCard>
        </Section>

        {/* TREE VIEW */}
        <Section title="Tree View" description="Hierarchical data display">
          <ComponentCard title="Tree" code="className='tree'" wide>
            <div className="tree" style={{ width: '100%' }}>
              <div className="tree-item tree-item--selected">
                <span className="tree-toggle tree-toggle--expanded">▶</span>
                <span className="tree-icon"><Folder width={16} height={16} /></span>
                <span className="tree-label">src</span>
              </div>
              <div className="tree-children">
                <div className="tree-item">
                  <span className="tree-toggle tree-toggle--expanded">▶</span>
                  <span className="tree-icon"><Folder width={16} height={16} /></span>
                  <span className="tree-label">components</span>
                </div>
                <div className="tree-children">
                  <div className="tree-item">
                    <span className="tree-toggle" style={{ visibility: 'hidden' }}>▶</span>
                    <span className="tree-icon"><Code width={16} height={16} /></span>
                    <span className="tree-label">Button.jsx</span>
                  </div>
                  <div className="tree-item">
                    <span className="tree-toggle" style={{ visibility: 'hidden' }}>▶</span>
                    <span className="tree-icon"><Code width={16} height={16} /></span>
                    <span className="tree-label">Input.jsx</span>
                  </div>
                </div>
                <div className="tree-item">
                  <span className="tree-toggle" style={{ visibility: 'hidden' }}>▶</span>
                  <span className="tree-icon"><Code width={16} height={16} /></span>
                  <span className="tree-label">App.jsx</span>
                </div>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* SIDEBAR */}
        <Section title="Sidebar" description="Navigation sidebar">
          <ComponentCard title="Sidebar Navigation" code="className='sidebar'" wide>
            <div className="sidebar" style={{ width: '260px', height: '300px', position: 'relative' }}>
              <div className="sidebar-header">
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: '600', color: 'var(--text-primary)' }}>Shell Manager</div>
              </div>
              <div className="sidebar-content">
                <div className="sidebar-section">
                  <div className="sidebar-section-title">Navigation</div>
                  <nav className="sidebar-nav">
                    <a className="sidebar-nav-item sidebar-nav-item--active">
                      <span className="sidebar-nav-icon"><Home width={18} height={18} /></span>
                      <span>Dashboard</span>
                    </a>
                    <a className="sidebar-nav-item">
                      <span className="sidebar-nav-icon"><Server width={18} height={18} /></span>
                      <span>Shells</span>
                    </a>
                    <a className="sidebar-nav-item">
                      <span className="sidebar-nav-icon"><Settings width={18} height={18} /></span>
                      <span>Settings</span>
                    </a>
                  </nav>
                </div>
              </div>
            </div>
          </ComponentCard>
        </Section>

        {/* MOTION TOKENS */}
        <Section title="Motion Tokens" description="Animation duration and easing standards">
          <ComponentCard title="Duration Tokens" code="var(--duration-*)" wide>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', width: '100%' }}>
              {[
                { name: '--duration-instant', value: '50ms', desc: 'Micro-interactions' },
                { name: '--duration-fast', value: '100ms', desc: 'Buttons, toggles' },
                { name: '--duration-base', value: '150ms', desc: 'Standard transitions' },
                { name: '--duration-slow', value: '250ms', desc: 'Expanding content' },
                { name: '--duration-slower', value: '400ms', desc: 'Page transitions' }
              ].map(token => (
                <div key={token.name} style={{ padding: '12px', background: 'var(--surface-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--ansi-cyan)' }}>{token.name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: '600', margin: '4px 0' }}>{token.value}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{token.desc}</div>
                </div>
              ))}
            </div>
          </ComponentCard>

          <ComponentCard title="Easing Tokens" code="var(--ease-*)" wide>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', width: '100%' }}>
              {[
                { name: '--ease-out', value: 'cubic-bezier(0.16, 1, 0.3, 1)', desc: 'Decelerating (entering)' },
                { name: '--ease-in-out', value: 'cubic-bezier(0.4, 0, 0.2, 1)', desc: 'Symmetric (morphing)' },
                { name: '--ease-spring', value: 'cubic-bezier(0.34, 1.56, 0.64, 1)', desc: 'Bouncy (playful)' },
                { name: '--ease-smooth', value: 'cubic-bezier(0.25, 0.1, 0.25, 1)', desc: 'Linear-ish (progress)' }
              ].map(token => (
                <div key={token.name} style={{ padding: '12px', background: 'var(--surface-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--ansi-cyan)' }}>{token.name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', margin: '4px 0', wordBreak: 'break-all' }}>{token.value}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-primary)' }}>{token.desc}</div>
                </div>
              ))}
            </div>
          </ComponentCard>
        </Section>

        {/* SPACING TOKENS */}
        <Section title="Spacing Tokens" description="Consistent spacing scale (4px base unit)">
          <ComponentCard title="Space Scale" code="var(--space-*)" wide>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              {[
                { name: '--space-1', value: '4px' },
                { name: '--space-2', value: '8px' },
                { name: '--space-3', value: '12px' },
                { name: '--space-4', value: '16px' },
                { name: '--space-5', value: '20px' },
                { name: '--space-6', value: '24px' },
                { name: '--space-7', value: '32px' },
                { name: '--space-8', value: '40px' }
              ].map(token => (
                <div key={token.name} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '120px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--ansi-cyan)' }}>{token.name}</div>
                  <div style={{ width: token.value, height: '24px', background: 'var(--accent-primary)', borderRadius: 'var(--radius-sm)' }}></div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{token.value}</div>
                </div>
              ))}
            </div>
          </ComponentCard>
        </Section>

        {/* RADIUS TOKENS */}
        <Section title="Radius Tokens" description="Border radius scale">
          <ComponentCard title="Radius Scale" code="var(--radius-*)">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
              {[
                { name: 'xs', value: '4px' },
                { name: 'sm', value: '6px' },
                { name: 'md', value: '8px' },
                { name: 'lg', value: '12px' },
                { name: 'xl', value: '16px' },
                { name: 'full', value: '9999px' }
              ].map(token => (
                <div key={token.name} style={{ textAlign: 'center' }}>
                  <div style={{ width: '48px', height: '48px', background: 'var(--accent-primary)', borderRadius: `var(--radius-${token.name})`, margin: '0 auto 8px' }}></div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-primary)' }}>{token.name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{token.value}</div>
                </div>
              ))}
            </div>
          </ComponentCard>
        </Section>

        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '48px 24px', borderTop: '1px solid var(--surface-border)', marginTop: '48px' }}>
          <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            Shell Design System v5.0 • Terminal-first • Mobile-optimized • Flat Design
          </p>
          <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 'var(--text-xs)', marginTop: '8px' }}>
            No shadows • No glows • 44-48px touch targets • Consistent token usage
          </p>
        </footer>
      </div>
    </MinimalBackgroundSystem>
  );
};

export default DesignSystemShowcase;
